import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, Users, Phone, MapPin, PawPrint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState, Loading } from "@/components/common";
import ClientFormDialog from "@/components/ClientFormDialog";
import { api, errMsg } from "@/lib/api";

export default function Clients() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [clients, setClients] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(params.get("new") === "1");

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/clients", { params: { q, archived } }).then((r) => setClients(r.data)).catch((e) => setError(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [q, archived]);

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v && params.get("new")) setParams({});
  };

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients" subtitle="Everyone you clean for, in one place." actions={<Button onClick={() => setOpen(true)} className="h-11" data-testid="new-client-btn"><Plus className="h-4 w-4 mr-1" />New client</Button>} />
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name, email, phone or address" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11 bg-white" data-testid="client-search-input" />
        </div>
        <Tabs value={archived ? "archived" : "active"} onValueChange={(v) => setArchived(v === "archived")}>
          <TabsList className="h-11">
            <TabsTrigger value="active" className="h-9" data-testid="clients-active-tab">Active</TabsTrigger>
            <TabsTrigger value="archived" className="h-9" data-testid="clients-archived-tab">Archived</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {error && <p className="text-red-700 text-sm">{error}</p>}
      {!clients ? <Loading /> : clients.length === 0 ? (
        <EmptyState icon={Users} title={q ? "No matching clients" : archived ? "No archived clients" : "No clients yet"} text="Add your first client to start scheduling jobs and sending estimates." testId="clients-empty" />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="clients-list">
          {clients.map((c) => (
            <Link key={c.id} to={`/clients/${c.id}`} data-testid={`client-row-${c.id}`} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 px-5 py-4 hover:bg-slate-50 transition-colors">
              <div className="sm:w-1/4 font-medium text-slate-900">{c.name}</div>
              <div className="flex-1 text-sm text-slate-500 flex items-center gap-1.5 min-w-0"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{c.address || "No address"}</span></div>
              <div className="text-sm text-slate-500 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{c.phone || "—"}</div>
              {c.pets && <div className="text-xs text-amber-800 flex items-center gap-1"><PawPrint className="h-3.5 w-3.5" />Pets</div>}
            </Link>
          ))}
        </div>
      )}
      <ClientFormDialog open={open} onOpenChange={onOpenChange} onSaved={(c) => setClients((l) => [c, ...(l || [])])} />
    </div>
  );
}
